import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Urls } from 'shared/constants/urls';
import { themePrimary, themeTextSecondary } from 'shared/utils/cssUtility';
import { useHighlightColor } from 'shared/utils/hooks/useHighlightColor';
import { useMobile } from 'shared/utils/hooks/useMobile';
import {
  LinkIcon,
  HomeIcon,
  TasksIcon,
  CartIcon,
  ChangeIcon,
  ChatIcon,
  DocumentIcon,
  SettingsIcon,
  NotificationIcon,
  TestTubeIcon,
  InfoIcon
} from 'shared/components/Icons';
import { Logo } from 'shared/components/Logo';
import { Text } from 'shared/components/Text';
import { Sidebar, TContent } from '../../';
import { getTasksData, getTasksSelector } from 'store/tasks/tasks.redux';
import {
  getNotificationsData,
  getNotificationsSelector
} from 'store/notifications/notification.redux';
import {
  getAppConfigSelector,
  getWhiteLabelAffiliateSelector
} from 'store/app/app.redux';
import {
  getUnreadCountSelector,
  getUnreadCount
} from 'store/patientConversation/patientConversation.redux';
import { useTranslationCustom } from 'shared/utils/hooks/useTranslationCustom';
import { ECOMMERCE_PLATFORMS } from 'shared/enums';
import { SidebarItems } from 'shared/constants/sidebarItems';
import { INTEGRATION_TYPES } from 'shared/constants/integrations';
import { isBrelloAffiliate } from 'shared/constants/affiliates';
import { getBrelloAppUrl } from 'shared/utils/brelloApp';

interface INavigation {
  showContent: boolean;
  setShowContent: (state: boolean) => void;
}

export const Navigation: FC<INavigation> = ({
  showContent,
  setShowContent
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const appConfig = useSelector(getAppConfigSelector);
  const [getIconColor, getHighLightState] = useHighlightColor(
    themeTextSecondary,
    themePrimary
  );
  const notifications = useSelector(getNotificationsSelector);
  const tasksData = useSelector(getTasksSelector);
  const unreadCount = useSelector(getUnreadCountSelector);
  const whiteLabelAffiliate = useSelector(getWhiteLabelAffiliateSelector);
  const [getTextColor] = useHighlightColor('text-secondary', 'text-primary');
  const [isNotifications, setIsNotifications] = useState(false);
  const taskCounter = tasksData.length;
  const t = useTranslationCustom();

  const messageCenterLabel =
    whiteLabelAffiliate?.integrationConfigurations?.find(
      (config) =>
        config.integration.title === INTEGRATION_TYPES.MESSAGE_CENTER_OVERRIDE
    )?.configurationData?.label || t('sidebar.messageCenter', 'Message Center');

  // append the custom links to the end of the navigation sidebar
  const getCustomLinks = () => {
    const customLinks = [];

    const dynamicLinksData =
      whiteLabelAffiliate?.integrationConfigurations?.find(
        (integrationConfiguration) =>
          integrationConfiguration.integration.title ===
          INTEGRATION_TYPES.DYNAMIC_LINKS
      )?.configurationData?.links;

    if (dynamicLinksData) {
      for (const key in dynamicLinksData) {
        const { url, label, renderLocation } = dynamicLinksData[key];

        // asserts the dynamicLinksData url to type "Urls"
        const enumeratedUrl = Urls[url as keyof typeof Urls];

        customLinks.push({
          icon: <LinkIcon color={getIconColor(enumeratedUrl)} />,
          text: <Text type="sf-body">{label}</Text>,
          onClick: async () => {
            window.location.replace(url);
          },
          visible: renderLocation === 'mainMenu',
          highlight: getHighLightState(enumeratedUrl)
        });
      }
    }

    return customLinks;
  };

  useEffect(() => {
    dispatch(getNotificationsData());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getTasksData());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getUnreadCount());
  }, [dispatch]);

  const sidebarContent: TContent = {
    header: [
      {
        icon: (
          <Logo
            Logo={
              appConfig.logoWithName && showContent
                ? appConfig.logo
                : appConfig.smallLogo || appConfig.logo
            }
            appName={appConfig.appName}
            width={32}
            height={32}
            className={'menu_logo'}
          />
        ),
        text: !appConfig.logoWithName ? (
          <Text type="m-heading" color={getTextColor(Urls.DashBoard)}>
            {appConfig.appName}
          </Text>
        ) : (
          <></>
        ),
        onClick: async () => {
          await import('../../../../../pages/DashBoardPage');
          appConfig.logoRedirectUrl
            ? window.open(appConfig.logoRedirectUrl, '_self')
            : navigate(Urls.DashBoard);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: true
      },
      {
        icon: (
          <HomeIcon
            width={12}
            height={12}
            color={getIconColor(Urls.DashBoard)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.DashBoard)}>
            {t('sidebar.dashboard', 'Dashboard')}
          </Text>
        ),
        onClick: async () => {
          await import('../../../../../pages/DashBoardPage');
          navigate(Urls.DashBoard);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(SidebarItems.DashBoard),
        highlight: getHighLightState(Urls.DashBoard)
      }
    ],
    bodyTop: [
      {
        icon: (
          <CartIcon
            width={12}
            height={12}
            color={getIconColor(Urls.OrdersHistory)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.OrdersHistory)}>
            {t('sidebar.ordersHistory', 'Orders History')}
          </Text>
        ),
        onClick: async () => {
          await import('../../../../../pages/OrdersHistoryPage');
          navigate(Urls.OrdersHistory);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible:
          whiteLabelAffiliate?.ecommerceModuleEnabled &&
          !appConfig.hiddenNavTabs?.includes(SidebarItems.OrdersHistory),
        highlight: getHighLightState(Urls.OrdersHistory)
      },
      {
        icon: (
          <CartIcon
            width={12}
            height={12}
            color={getIconColor(Urls.SubscriptionsHistory)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.SubscriptionsHistory)}>
            {t('sidebar.subscriptionsHistory', 'Subscriptions History')}
          </Text>
        ),
        onClick: async () => {
          await import('../../../../../pages/SubscriptionsHistoryPage');
          navigate(Urls.SubscriptionsHistory);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible:
          whiteLabelAffiliate?.ecommerceModuleEnabled &&
          whiteLabelAffiliate?.ecommercePlatform !==
            ECOMMERCE_PLATFORMS.Shopify &&
          !appConfig.hiddenNavTabs?.includes(SidebarItems.SubscriptionsHistory),
        highlight: getHighLightState(Urls.SubscriptionsHistory)
      },
      {
        icon: (
          <ChangeIcon
            width={12}
            height={12}
            color={getIconColor(Urls.VisitHistory)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.VisitHistory)}>
            {t('sidebar.visitsHistory', 'Visits History')}
          </Text>
        ),
        onClick: async () => {
          await import('../../../../../pages/ServiceHistoryPage');
          navigate(Urls.VisitHistory);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(SidebarItems.VisitHistory),
        highlight: getHighLightState(Urls.VisitHistory)
      },
      {
        icon: (
          <TestTubeIcon
            width={12}
            height={12}
            color={getIconColor(Urls.LabOrdersHistory)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.LabOrdersHistory)}>
            {t('sidebar.labOrdersHistory', 'Lab Orders History')}
          </Text>
        ),
        onClick: async () => {
          await import('../../../../../pages/LabOrdersHistory');
          navigate(Urls.LabOrdersHistory);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(
          SidebarItems.LabOrdersHistory
        ),
        highlight: getHighLightState(Urls.LabOrdersHistory)
      },
      {
        icon: (
          <ChatIcon
            width={12}
            height={12}
            color={getIconColor(Urls.Messages)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.Messages)}>
            {messageCenterLabel}
          </Text>
        ),
        count: unreadCount,
        onClick: async () => {
          await import('../../../../../pages/MessagesPage');
          navigate(Urls.Messages);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(SidebarItems.Messages),
        highlight: getHighLightState(Urls.Messages)
      },
      {
        icon: (
          <NotificationIcon
            width={12}
            height={12}
            color={getIconColor(Urls.Notifications)}
          />
        ),
        text: (
          <Text
            type="sf-body"
            color={getTextColor(Urls.Notifications)}
            key="notifications"
          >
            {t('sidebar.notifications', 'Notifications')}
          </Text>
        ),
        count: notifications.length,
        onClick: async () => {
          await import('../../../../../pages/NotificationPage');
          navigate(Urls.Notifications);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(SidebarItems.Notifications),
        highlight: getHighLightState(Urls.Notifications)
      },
      {
        icon: (
          <TasksIcon width={12} height={12} color={getIconColor(Urls.Tasks)} />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.Tasks)}>
            {t('sidebar.taskCenter', 'Task Center')}
          </Text>
        ),
        count: taskCounter,
        onClick: async () => {
          await import('../../../../../pages/TasksPage');
          navigate(Urls.Tasks);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(SidebarItems.Tasks),
        highlight: getHighLightState(Urls.Tasks)
      },
      {
        icon: (
          <DocumentIcon
            width={12}
            height={12}
            color={getIconColor(Urls.Documents)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.Documents)}>
            {t('sidebar.documents', 'Documents')}
          </Text>
        ),
        count: 0,
        onClick: async () => {
          await import('../../../../../pages/DocumentsPage');
          navigate(Urls.Documents);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(SidebarItems.Documents),
        highlight: getHighLightState(Urls.Documents)
      },
      {
        icon: (
          <InfoIcon
            width={12}
            height={12}
            color={getIconColor(Urls.Education)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.Education)}>
            {t('sidebar.education', 'Education')}
          </Text>
        ),
        count: 0,
        onClick: async () => {
          await import('../../../../../pages/EducationPage');
          navigate(Urls.Education);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(SidebarItems.Education),
        highlight: getHighLightState(Urls.Education)
      },
      {
        icon: (
          <InfoIcon
            width={12}
            height={12}
            color={getIconColor(Urls.ProviderGroupInformation)}
          />
        ),
        text: (
          <Text
            type="sf-body"
            color={getTextColor(Urls.ProviderGroupInformation)}
          >
            {t(
              'sidebar.providerGroupInformation',
              'Provider Group Information'
            )}
          </Text>
        ),
        count: 0,
        onClick: async () => {
          await import('../../../../../pages/ProviderGroupInformationPage');
          navigate(Urls.ProviderGroupInformation);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: true,
        highlight: getHighLightState(Urls.ProviderGroupInformation)
      },
      {
        icon: <LinkIcon color={themeTextSecondary} />,
        text: (
          <Text type="sf-body" color="text-secondary">
            {t('sidebar.brelloApp', 'Get the Brello App')}
          </Text>
        ),
        count: 0,
        onClick: async () => {
          // Leaves the portal for the app or the store, so no route change.
          window.location.href = getBrelloAppUrl();
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible:
          isBrelloAffiliate(whiteLabelAffiliate?.id) &&
          !appConfig.hiddenNavTabs?.includes(SidebarItems.BrelloApp),
        highlight: false
      },
      ...getCustomLinks()
    ],
    bodyBottom: [
      {
        icon: (
          <SettingsIcon
            width={12}
            height={12}
            color={getIconColor(Urls.Settings)}
          />
        ),
        text: (
          <Text type="sf-body" color={getTextColor(Urls.Settings)}>
            {t('sidebar.settings', 'Settings')}
          </Text>
        ),
        onClick: async () => {
          await import('../../../../../pages/SettingsPage');
          navigate(Urls.Settings);
          isMobile && setShowContent(false);
          setIsNotifications(false);
        },
        visible: !appConfig.hiddenNavTabs?.includes(SidebarItems.Settings),
        highlight: getHighLightState(Urls.Settings)
      }
    ]
  };
  return (
    <Sidebar
      showContent={showContent}
      setShowContent={setShowContent}
      content={sidebarContent}
    />
  );
};
